import styled from "@emotion/styled"
import { PInputText, PText } from "@porsche-design-system/components-react"
import { colorContrastLow, spacingStaticLg, spacingStaticMd } from "@porsche-design-system/components-react/emotion"
import { defineMessages, FormattedMessage, useIntl } from "react-intl"
import { CopyToClipboardButton, Skeleton } from "src/components"
import { ErrorNotificationInline } from "src/context/errorHandler/ErrorNotificationInline"

const messages = defineMessages({
	copiedToClipboard: {
		defaultMessage: "Link was successfully added to clipboard.",
		id: "inventorylist.shareVehicles.copiedToClipboard",
	},
	copyLinkHeadline: {
		defaultMessage: "Copy the link to share your vehicle selection:",
		id: "inventorylist.shareVehicles.copyLinkHeadline",
	},
	linkExplanationText: {
		defaultMessage: "The link leads your customer directly to the selected vehicles.",
		id: "inventorylist.shareVehicles.linkExplanationText",
	},
	scanQrCodeHeadline: {
		defaultMessage: "Alternatively, scan the QR code:",
		id: "inventorylist.shareVehicles.scanQrCodeHeadline",
	},
})

interface ShareVehiclesLinkAndQRCodeProps {
	onCopyToClipboard?: () => void
	qrCodeData?: string
	url: string
}

export const ShareVehiclesLinkAndQRCode = ({ onCopyToClipboard, qrCodeData, url }: ShareVehiclesLinkAndQRCodeProps) => {
	const { formatMessage } = useIntl()

	return (
		<Layout>
			<Section>
				<div>
					<PText weight="semibold">
						<FormattedMessage {...messages.copyLinkHeadline} />
					</PText>
					<PText size="xs" color="contrast-medium">
						<FormattedMessage {...messages.linkExplanationText} />
					</PText>
				</div>

				<PInputText name="url" value={url} readOnly>
					<CopyToClipboardButton
						slot="end"
						value={url}
						successToastText={formatMessage(messages.copiedToClipboard)}
						onCopied={onCopyToClipboard}
					/>
				</PInputText>
			</Section>

			<Section>
				<PText weight="semibold">
					<FormattedMessage {...messages.scanQrCodeHeadline} />
				</PText>
				{ErrorNotificationInline({ dismissButton: false }) ??
					(qrCodeData ? (
						<CenterImg src={qrCodeData} height={200} width={200} alt="" />
					) : (
						<Center as={QrCodeSkeleton} />
					))}
			</Section>
		</Layout>
	)
}

const Layout = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${spacingStaticLg};
`

const Section = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${spacingStaticMd};

	&:not(:first-of-type) {
		padding: ${spacingStaticLg} 0 0;
		border-top: 1px solid ${colorContrastLow};
	}
`

const Center = styled.div`
	align-self: center;
`
const CenterImg = Center.withComponent("img")

const QrCodeSkeleton = styled(Skeleton.Picture)`
	aspect-ratio: 1 / 1;
	width: 200px;
`
